/*
Copyright (c) Uber Technologies, Inc.

This source code is licensed under the MIT license found in the
LICENSE file in the root directory of this source tree.
*/
import { mapV1PrimitiveToV2, mapV1SemanticToV2 } from '@uber/base-specs/src/utils/mapV1ToV2Colors';
import type { V1PrimitiveTokenKey } from '@uber/base-specs/src/utils/mapV1ToV2Colors';
import type { ThemeMode } from '../../types';
import { DARK_COMPONENT_COLOR_PROVENANCE, LIGHT_COMPONENT_COLOR_PROVENANCE } from './component-color-provenance';

/**
 * Upgrades v1 ComponentColors tokens (e.g. `tagPrimarySolidFont:
 * primitiveDarkColors.gray900Dark`, or `bottomNavigationSelectedText:
 * semanticColors.contentPrimary`) to their v2 equivalent.
 *
 * Component-color tokens can't be upgraded by key like `upgradeV1ColorsToV2`
 * does for primitives/semantics, nor by their *resolved* value — a
 * component color only stores the resolved hex/CSS-var string of whichever
 * primitive or semantic it was built from, and that resolved value is
 * frequently shared by several unrelated v1 names whose v2 targets don't
 * agree (e.g. `#000000` is simultaneously `black`, `brandAi0`, and — in the
 * light theme — `contentPrimary`; guessing from the value alone can silently
 * pick the wrong one). Instead, this uses `component-color-provenance.ts`, a
 * table statically extracted from the actual
 * `primitiveDarkColors.X`/`primitiveLightColors.X`/`semanticColors.X`
 * references in `color-component-tokens.ts`, so every component color is
 * routed through the *same* source token — primitive or semantic — it was
 * genuinely authored from, then upgraded via the matching v1 → v2 table.
 *
 * Same override contract as `upgradeV1ColorsToV2`: a token whose current
 * value no longer matches the `v1Colors` baseline is assumed to be an
 * explicit consumer override and is left untouched — the upgrade only ever
 * fires for tokens still at their default.
 *
 * Exported for tests; internal.
 */
export function upgradeV1ComponentColorsToV2(themeColors: Record<string, unknown>, v1Colors: Record<string, unknown>, v2Colors: Record<string, string>, themeMode: ThemeMode | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = Object.assign({}, themeColors);
  applyV1ComponentColorUpgrades(out, v1Colors, v2Colors, themeMode);
  return out;
}

/**
 * In-place form of {@link upgradeV1ComponentColorsToV2}: upgrades component
 * colors directly into `out` instead of returning a copy.
 *
 * Needs no separate pre-upgrade baseline, unlike {@link applyV1ColorUpgrades}.
 * Component colors are disjoint from the primitive and semantic keys the other
 * passes rewrite, so `out` still holds their original values and is itself
 * what the override check compares against.
 */
export function applyV1ComponentColorUpgrades(out: Record<string, unknown>, v1Colors: Record<string, unknown>, v2Colors: Record<string, string>, themeMode: ThemeMode | undefined): void {
  const provenanceTable = themeMode === 'dark' ? DARK_COMPONENT_COLOR_PROVENANCE : LIGHT_COMPONENT_COLOR_PROVENANCE;
  for (const key of Object.keys(provenanceTable)) {
    const v1Value = v1Colors[key];
    if (typeof v1Value !== 'string') continue;
    if (out[key] !== v1Value) continue;
    const provenance = provenanceTable[key];
    const v2Key = provenance.kind === 'primitive' ? mapV1PrimitiveToV2((provenance.sourceKey as V1PrimitiveTokenKey)) : mapV1SemanticToV2(provenance.sourceKey);
    if (!v2Key) continue;
    const v2Value = v2Colors[v2Key];
    if (v2Value === undefined) continue;
    out[key] = v2Value;
  }
}